<template>
  <aside class="flex h-full flex-col gap-2 overflow-y-auto p-[8px_16px_24px_16px] xs:p-[16px_24px_24px_24px]">
    <ui-list-date-selector-tab v-show="hazards.mapMode !== MapMode.Default" />
    <ui-list-section
      v-for="sectionType in selectedListSections"
      :key="sectionType"
      :section-type="sectionType"
      :fire-danger-ratings="hazards.mapMode === MapMode.FireDanger"
      :filtered-hazards="filteredHazards[sectionType]"
    />
    <ui-list-more-alert
      v-if="!hazards.isLoadingHazards && hazards.mapMode === MapMode.Default"
      :hidden-warnings="hiddenWarnings"
      :hidden-incidents="hiddenIncidents"
    />
  </aside>
</template>

<script setup lang="ts">
import { HazardEntity, HazardType, MapMode, TotalFireBanEntity, UiListSectionType, useHazardsStore, useMapStore } from '@/features/hazards';
import { computed, unref } from 'vue';
import UiListSection from './UiListSection.vue';

const hazards = useHazardsStore();
const map = useMapStore();

defineProps<{
  hazardTypes: HazardType[];
}>();

const hiddenWarnings = computed(() => {
  if (!hazards.filteredSectionHazards) {
    return 0;
  }
  return hazards.filteredSectionHazards.warnings.length - filteredHazards.value[UiListSectionType.Warnings].length;
});

const hiddenIncidents = computed(() => {
  if (!hazards.filteredSectionHazards) {
    return 0;
  }
  return hazards.filteredSectionHazards.incidents.length - filteredHazards.value[UiListSectionType.Incidents].length;
});

const filteredHazards = computed<Record<UiListSectionType, HazardEntity[]>>(() => {
  const allHazards = hazards.filteredSectionHazards;

  const visibleHazards = unref(map.visibleHazards);

  const visibleWarnings =
    hazards.mapMode === MapMode.Default
      ? allHazards.warnings.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visibleIncidents =
    hazards.mapMode === MapMode.Default
      ? allHazards.incidents.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visibleOutagesAndClosures =
    hazards.mapMode === MapMode.Default
      ? allHazards.outagesAndClosures.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visibleCentres =
    hazards.mapMode === MapMode.Default
      ? allHazards.centres.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visiblePrescribedBurnsAndBurnOffs =
    hazards.mapMode === MapMode.Default
      ? allHazards.prescribedBurnsAndBurnOffs.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visibleNaturalHazards =
    hazards.mapMode === MapMode.Default
      ? allHazards.naturalHazards.filter((hazard) => visibleHazards.some(({ properties }) => properties?.id === hazard.id))
      : [];

  const visibleFireDangerRatings =
    hazards.mapMode === MapMode.FireDanger
      ? allHazards.fireDangerRatings.filter((hazard) =>
          visibleHazards.some(({ properties }) => properties?.name === hazard.pointFeature?.properties?.DIST_NAME)
        )
      : [];

  const visibleFireBans =
    hazards.mapMode === MapMode.FireBan
      ? (allHazards.totalFireBans as TotalFireBanEntity[]).filter((hazard) =>
          visibleHazards.some(({ properties }) => properties?.name === hazard.rawLgaName)
        )
      : [];

  return {
    [UiListSectionType.Warnings]: visibleWarnings,
    [UiListSectionType.Incidents]: visibleIncidents,
    [UiListSectionType.OutagesAndClosures]: visibleOutagesAndClosures,
    [UiListSectionType.Centres]: visibleCentres,
    [UiListSectionType.PrescribedBurnsAndBurnOffs]: visiblePrescribedBurnsAndBurnOffs,
    [UiListSectionType.NaturalHazards]: visibleNaturalHazards,
    [UiListSectionType.FireDangerRatings]: visibleFireDangerRatings,
    [UiListSectionType.TotalFireBans]: visibleFireBans,
  };
});

const defaultListSections = [
  UiListSectionType.Warnings,
  UiListSectionType.Centres,
  UiListSectionType.OutagesAndClosures,
  UiListSectionType.Incidents,
  UiListSectionType.NaturalHazards,
  UiListSectionType.PrescribedBurnsAndBurnOffs,
];

const fireDangerRatingsListSections = [UiListSectionType.FireDangerRatings];

const totalFireBansListSections = [UiListSectionType.TotalFireBans];

const selectedListSections = computed(() => {
  switch (hazards.mapMode) {
    case MapMode.Default:
      return defaultListSections;
    case MapMode.FireDanger:
      return fireDangerRatingsListSections;
    case MapMode.FireBan:
      return totalFireBansListSections;
    default:
      return [];
  }
});
</script>
